import { Button, Tag, Tooltip, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState, useRef, type ReactNode } from 'react';
import SettingNav from '../../ui/layout/SettingNav';
import WorkspaceHeader from '../../ui/layout/WorkspaceHeader';
import styles from './UserSettings.module.scss';
import { useNavigate } from 'react-router-dom';
import { useUserProfile } from '../../context/UserProfileContext';
import CardTable from '../../components/Table/CardTable';
import NewButton from '../../components/NewButton/NewButton';
import ConfirmDialog from '../../components/ConfirmDialog/ConfirmDialog';
import { useClients } from '../../context/ClientContext';
import { addAdminAccess, getUsersByTenant, removeAdminAccess, removeUserAccess, type FetchPageOptions } from '../../infrastructure/apiClient';
import type { UserDetails } from '../../infrastructure/models';
import { DeleteOutlined, UserAddOutlined, UserDeleteOutlined } from '@ant-design/icons';
import AssignUser from './AssignUser';
import { getNextPageAfterDelete } from '../../utils/pagination';
import { useTableState } from '../../hooks/useTableState';
import { sortData, sortConfig, type SortConfig } from '../../utils/sortUtils';

interface UserSettingsProps {
  embedded?: boolean;
}

const SORT_CONFIGS: SortConfig<UserDetails>[] = [
  sortConfig('name', 'string'),
  sortConfig('email', 'string'),
  sortConfig('isAdmin', 'boolean'),
  sortConfig('isActive', 'boolean'),
  sortConfig('addedAt', 'date'),
];

const UserSettings = ({ embedded = false }: UserSettingsProps) => {
  const [active, setActive] = useState<'user' | 'tenant'>('user');
  const navigate = useNavigate();
  const { profile } = useUserProfile();
  const { activeClient } = useClients();
  const signedInEmail = profile?.email?.trim().toLowerCase() ?? '';
  const [users, setUsers] = useState<UserDetails[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAssignUser, setShowAssignUser] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [confirmTitle, setConfirmTitle] = useState('');
  const [confirmMessage, setConfirmMessage] = useState<string | ReactNode>('');
  const [confirmAction, setConfirmAction] = useState<'make-admin' | 'remove-admin' | 'remove-user' | null>(null);
  const [confirmUserId, setConfirmUserId] = useState<string | null>(null);

  const fetchUsers = useCallback(
    async (tenantId: string, { offset = 0, limit = 10, signal }: FetchPageOptions = {}) => {
      setLoading(true);
      setError(null);
      try {
        const response = await getUsersByTenant(tenantId, { offset, limit, signal });
        const list = response?.data ?? [];
        setUsers(list);
        updateCurrentPage(Math.floor(offset / limit) + 1);
        updateTotal(typeof response?.total === 'number' ? response.total : 0);
      } catch (err) {
        if (!signal?.aborted) {
          setUsers([]);
          resetPagination();
          setError('Unable to load users for this tenant.');
          message.error('Failed to load users');
        }
      } finally {
        if (!signal?.aborted) {
          setLoading(false);
        }
      }
    },
    [],
  );

  const handlePageChange = useCallback((offset: number, limit: number) => {
    if (!activeClient?.id) return;
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    fetchUsers(activeClient.id, { offset, limit, signal: controller.signal });
  }, [activeClient?.id, fetchUsers]);

  const {
    pagination,
    sorter,
    tablePagination,
    handleTableChange,
    resetPagination,
    updateTotal,
    updateCurrentPage,
    getOffset,
  } = useTableState<UserDetails>({ onPageChange: handlePageChange });

  const handleMakeAdmin = useCallback(async (id: string) => {
    try {
      const updatedUser = await addAdminAccess(id);
      setUsers((prev) => prev.map((user) => (user.id === id ? { ...user, ...updatedUser } : user)));
      message.success('Admin access granted');
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Failed to grant admin access');
    }
  }, []);

  const handleRemoveAdmin = useCallback(async (id: string) => {
    try {
      const updatedUser = await removeAdminAccess(id);
      setUsers((prev) => prev.map((user) => (user.id === id ? { ...user, ...updatedUser } : user)));
      message.success('Admin access removed');
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Failed to remove admin access');
    }
  }, []);

  const columns: ColumnsType<UserDetails> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => (a.name ?? '').localeCompare(b.name ?? ''),
      sortOrder: sorter.columnKey === 'name' ? sorter.order : null,
    },
    {
      title: 'Email Address',
      dataIndex: 'email',
      key: 'email',
      sorter: (a, b) => (a.email ?? '').localeCompare(b.email ?? ''),
      sortOrder: sorter.columnKey === 'email' ? sorter.order : null,
    },
    {
      title: 'Role',
      dataIndex: 'isAdmin',
      key: 'isAdmin',
      sorter: (a, b) => Number(Boolean(a.isAdmin)) - Number(Boolean(b.isAdmin)),
      sortOrder: sorter.columnKey === 'isAdmin' ? sorter.order : null,
      render: (value: UserDetails['isAdmin']) => (
        <Tag className={`!mr-0 ${value ? styles.adminTag : ''}`}>
          {value ? 'Admin' : 'General'}
        </Tag>
      ),
    },
    {
      title: 'Active',
      dataIndex: 'isActive',
      key: 'isActive',
      sorter: (a, b) => Number(Boolean(a.isActive)) - Number(Boolean(b.isActive)),
      sortOrder: sorter.columnKey === 'isActive' ? sorter.order : null,
      render: (value: UserDetails['isActive']) => (
        <Tag className="!mr-0" color={value === false ? 'default' : 'green'}>
          {value === false ? 'Inactive' : 'Active'}
        </Tag>
      ),
    },
    {
      title: 'Added On',
      dataIndex: 'addedAt',
      key: 'addedAt',
      sorter: (a, b) => dayjs(a.addedAt).valueOf() - dayjs(b.addedAt).valueOf(),
      sortOrder: sorter.columnKey === 'addedAt' ? sorter.order : null,
      render: (value: string) => dayjs(value).format('MMM D, YYYY h:mm A'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_value, record) => (
        (() => {
          const recordEmail = record.email?.trim().toLowerCase() ?? '';
          const isSelf = Boolean(signedInEmail && recordEmail && recordEmail === signedInEmail);
          return (
        <div className="flex gap-2">
          <Tooltip title="Make Admin">
            <Button
              type="text"
              icon={<UserAddOutlined />}
              aria-label="Make Admin"
              disabled={record.isAdmin || isSelf}
              onClick={() => handleOpenConfirm('make-admin', record)}
            />
          </Tooltip>
          <Tooltip title="Remove as admin">
            <Button
              type="text"
              icon={<UserDeleteOutlined />}
              aria-label="Remove as admin"
              disabled={!record.isAdmin || isSelf}
              onClick={() => handleOpenConfirm('remove-admin', record)}
            />
          </Tooltip>
          <Tooltip title="Remove User">
            <Button
              type="text"
              icon={<DeleteOutlined />}
              aria-label="Remove User"
              disabled={record.isActive === false || isSelf}
              onClick={() => handleOpenConfirm('remove-user', record)}
            />
          </Tooltip>
        </div>
          );
        })()
      ),
    },
  ];

  const handleMakeInactive = useCallback(
    async (id: string) => {
      if (!activeClient?.id) {
        message.error('No active tenant selected');
        return;
      }
      try {
        await removeUserAccess(activeClient.id, id);
        const { nextPage, offset } = getNextPageAfterDelete({
          currentPage: pagination.current,
          pageSize: pagination.pageSize,
          totalItems: pagination.total,
          deletedItems: 1,
        });
        updateCurrentPage(nextPage);
        abortControllerRef.current?.abort();
        const controller = new AbortController();
        abortControllerRef.current = controller;
        await fetchUsers(activeClient.id, { offset, limit: pagination.pageSize, signal: controller.signal });
        message.success('User removed');
      } catch (err) {
        message.error(err instanceof Error ? err.message : 'Failed to remove user');
      }
    },
    [activeClient?.id, fetchUsers, pagination.current, pagination.pageSize, pagination.total, updateCurrentPage],
  );

  const handleOpenConfirm = useCallback((action: 'make-admin' | 'remove-admin' | 'remove-user', record: UserDetails) => {
    const name = record.name ?? 'this user';
    if (action === 'make-admin') {
      setConfirmTitle('Grant admin access');
      setConfirmMessage(`Are you sure you want to grant admin access to ${name}?`);
    } else if (action === 'remove-admin') {
      setConfirmTitle('Remove admin access');
      setConfirmMessage(`Are you sure you want to remove admin access from ${name}?`);
    } else {
      setConfirmTitle('Remove user');
      setConfirmMessage(`Are you sure you want to remove ${name} from this tenant?`);
    }
    setConfirmAction(action);
    setConfirmUserId(record.id);
    setConfirmOpen(true);
  }, []);

  const handleConfirmCancel = useCallback(() => {
    if (confirmLoading) return;
    setConfirmOpen(false);
    setConfirmAction(null);
    setConfirmUserId(null);
    setConfirmTitle('');
    setConfirmMessage('');
  }, [confirmLoading]);

  const handleConfirmAction = useCallback(async () => {
    if (!confirmAction || !confirmUserId) {
      handleConfirmCancel();
      return;
    }
    setConfirmLoading(true);
    try {
      if (confirmAction === 'make-admin') {
        await handleMakeAdmin(confirmUserId);
      } else if (confirmAction === 'remove-admin') {
        await handleRemoveAdmin(confirmUserId);
      } else {
        await handleMakeInactive(confirmUserId);
      }
    } finally {
      setConfirmLoading(false);
      handleConfirmCancel();
    }
  }, [confirmAction, confirmUserId, handleConfirmCancel, handleMakeAdmin, handleMakeInactive, handleRemoveAdmin]);

  useEffect(() => {
    if (!activeClient?.id) {
      setUsers([]);
      resetPagination();
      return;
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;
    fetchUsers(activeClient.id, { offset: 0, limit: pagination.pageSize, signal: controller.signal });
    return () => controller.abort();
  }, [activeClient?.id, fetchUsers, pagination.pageSize, resetPagination]);

  const sortedUsers = useMemo(() => sortData(users, sorter, SORT_CONFIGS), [sorter, users]);

  const handleAssigned = useCallback(() => {
    setShowAssignUser(false);
    if (!activeClient?.id) return;
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    fetchUsers(activeClient.id, { offset: getOffset(), limit: pagination.pageSize, signal: controller.signal });
  }, [activeClient?.id, fetchUsers, getOffset, pagination.pageSize]);

  const content = showAssignUser ? (
    <AssignUser onBack={() => setShowAssignUser(false)} onAssigned={handleAssigned} />
  ) : (
    <div className={styles.root}>
      <div className={styles.header}>
        <h1 className={styles.title}>User Dashboard</h1>
        <div className={styles.actions}>
          <NewButton label="Assign user" onClick={() => setShowAssignUser(true)} />
        </div>
      </div>
      <div className={styles.tableContainer}>
        <CardTable<UserDetails>
          className={styles.cardTable}
          title="Users"
          columns={columns}
          dataSource={sortedUsers}
          rowKey={(record) => record.id}
          loading={loading}
          pagination={tablePagination}
          onChange={handleTableChange}
          error={error}
        />
      </div>
      <ConfirmDialog
        open={confirmOpen}
        title={confirmTitle}
        message={confirmMessage}
        confirmText="Confirm"
        cancelText="Cancel"
        confirmLoading={confirmLoading}
        onConfirm={handleConfirmAction}
        onCancel={handleConfirmCancel}
      />
    </div>
  );

  if (embedded) {
    return content;
  }

  return (
    <div className={styles.page}>
      <WorkspaceHeader />
      <div className={styles.body}>
        <aside className={styles.nav}>
          <SettingNav active={active} onChange={(key) => setActive(key as typeof active)} onHome={() => navigate('/workspace')} />
        </aside>
        <div className={styles.content}>{content}</div>
      </div>
    </div>
  );
};

export default UserSettings;
